import React, { useRef, useState } from 'react';
import BusinessInformationSection from './BusinessInformationSection';
import AccountHolderInformationSection from './AccountHolderInformationSection';
import ConsultationTypeSection from './ConsultationTypeSection';
import BusinessEnergyNeedsSection from './BusinessEnergyNeedsSection';
import AdditionalCommentsSection from './AdditionalCommentsSection';
import { Grid } from '@material-ui/core';
import PGEButton from '../../buttons';
import useEnergyConsultationForm from '../../../hooks/useEnergyConsultationForm';
import { navigate } from '@reach/router';
import routes from '../../../routes';
import { useTranslation } from '../../../hooks/useTranslation';
import { AccountDetail } from '../../../__generated__/pge-types';
import CollapsiblePanel from '../CollapsiblePanel';
import Backdrop from '../../backdrop/Backdrop';

type Props = {
  defaultExpanded: boolean;
};

const NewConsultationForm: React.FC<Props> = ({ defaultExpanded = false }) => {
  const {
    loading,
    form,
    formKey,
    accounts,
    availableSiteVisitDays,
    isFormSubmitting,
    onSubmit,
  } = useEnergyConsultationForm({
    onSuccess: () => {
      setExpanded(false);
    },
  });
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const formRef = useRef<HTMLFormElement | null>(null);

  const onFormError = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  if (loading) {
    return <Backdrop forceOpen />;
  }

  if (accounts?.length === 0) {
    return null;
  }

  return (
    <CollapsiblePanel
      title={t('NEW_ENERGY_CONSULTATION_REQUEST')}
      subtitle={t('NEW_ENERGY_CONSULTATION_REQUEST_SUBTITLE')}
      expanded={expanded}
      setExpanded={setExpanded}
    >
      <form
        onSubmit={form.submit(onSubmit, e => onFormError())}
        key={formKey}
        ref={formRef}
      >
        <BusinessInformationSection
          form={form}
          accounts={accounts as AccountDetail[]}
        />

        <AccountHolderInformationSection form={form} />

        <ConsultationTypeSection
          form={form}
          availableSiteVisitDays={availableSiteVisitDays}
        />

        <BusinessEnergyNeedsSection form={form} />

        <AdditionalCommentsSection form={form} />

        <Grid container justify="flex-end">
          <Grid item css={{ display: 'flex', gap: '12px' }}>
            <PGEButton
              variant="outlined"
              onClick={() => navigate(routes.ACCOUNT)}
            >
              {t('CANCEL')}
            </PGEButton>
            <PGEButton type="submit" disabled={isFormSubmitting}>
              {isFormSubmitting ? t('SUBMITTING') : t('SUBMIT')}
            </PGEButton>
          </Grid>
        </Grid>
      </form>
    </CollapsiblePanel>
  );
};

export default NewConsultationForm;
